import { useEffect, useState } from 'react';
import { stakeSettingDefaultValue, useGetAccountSetting } from '@/hooks/query/use-account-settings-data';
import { BetDefaultStakeType, accountService } from '@/services/account.service';
import { useQueryClient } from '@tanstack/react-query';
import { bettingInputMaxLength } from '@/hooks/query';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';

export const useDefaultStake = (from: 'quick-settings' | 'me' | 'betslip') => {
	const queryClient = useQueryClient();
	const [selectedId, setSelectedId] = useState(-1);
	const [inputStake, setInputStake] = useState(0);
	const [isInputFocus, setIsInputFocus] = useState(false);
	const [isInputSelected, setIsInputSelected] = useState(false);
	const { userSetting } = useGetAccountSetting();
	const defaultStakeList = userSetting?.betInfo.betStakeList.map(b => ({ id: Number(b.Value), text: b.Text })) ?? [];
	const view = useAppearanceStore(state => state.view);
	const defaultStake = stakeSettingDefaultValue(view);
	
	useEffect(() => {
		if (userSetting?.betInfo) {
			if (userSetting.betInfo.betStakeID) {
				const defaultSelectedId = Number(userSetting?.betInfo.betStakeID);
				setSelectedId(defaultSelectedId);
			}

			let defaultStake = userSetting.betInfo.betStakeValue;
			if (defaultStake <= 0) {
				// WEB-13763 bet setting功能 customize選項 stake預設要為1
				defaultStake = 1;
			}

			setInputStake(defaultStake);
		}
	}, [userSetting?.betInfo.betStakeID, userSetting?.betInfo.betStakeValue]);

	useEffect(() => {
		setIsInputSelected(false);
	}, [inputStake]);

	useEffect(() => {
		if (isInputFocus) {
			setIsInputSelected(true);
		} else {
			setIsInputSelected(false);
		}
	}, [isInputFocus]);

	const changeSelectedId = (id: number) => () => {
		setKafka();
		setSelectedId(id);
		if (id !== BetDefaultStakeType.Customize) {
			accountService.setDefaultStake(queryClient, userSetting!, id);
		} else {
			setIsInputFocus(true);
		}
	}

	const toggleInput = (index: number) => setIsInputFocus(index === -1 ? false : true);

	const setSelectKey = (index: number) => setIsInputSelected(index === -1 ? false : true);

	const onNumberPress = (value: string) => {
		let stake = (inputStake ?? 0).toString();
		stake = stake.length + value.length > bettingInputMaxLength || isInputSelected
			? value
			: stake + value;
		setInputStake(Number(stake));
	}

	const onBackspacePress = () => {
		let stake = (inputStake ?? 0).toString();
		stake = isInputSelected ? '0' : stake.substring(0, stake.length - 1);
		setInputStake(Number(stake));
	}

	// mars: 值小於0跳出警告視窗
	// galaxy: 值小於0自動設定為defaultStake
	const saveCustomize = () => {
		setIsInputFocus(!isInputFocus)
		if (selectedId === BetDefaultStakeType.Customize) {
			if (inputStake > 0 && inputStake !== userSetting?.betInfo.betStakeValue) {
				accountService.setDefaultStake(queryClient, userSetting!, selectedId, inputStake);
			} else if (view === ViewEnum.galaxy && inputStake <= 0) {
				accountService.setDefaultStake(queryClient, userSetting!, selectedId, defaultStake);
			}
		}
	}

	const setKafka = () => {
		let kafkaData;
		if (from === 'betslip') {
			kafkaData = view === ViewEnum.galaxy ?
				{
					note: KafkaNote.GalaxyV2,
					type: 8,
					action: 3
				} : {
					note: KafkaNote.MarsV1,
					type: 10,
					action: 11
				};
		} else {
			kafkaData = view === ViewEnum.galaxy ?
				{
					note: KafkaNote.GalaxyV2,
					type: 11,
					action: 19
				} : {
					note: KafkaNote.MarsV1,
					type: 7,
					action: from === 'me' ? 30 : 16
				};
		}

		setKafkaNormal(kafkaData);
	}

	return {
		defaultStakeList,
		selectedId,
		changeSelectedId,
		inputStake,
		isInputFocus,
		toggleInput,
		isInputSelected,
		setSelectKey,
		onNumberPress,
		onBackspacePress,
		saveCustomize,
		setInputStake
	};
};
